import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Mail,
  User,
  Building2,
  Briefcase,
  Loader2,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
} from "lucide-react";
import LanguageSelector from "../components/LanguageSelector";
import { buildApiPath, API_ENDPOINTS, getApiUrl } from "../utils/api";
import axios from "axios";

const RegisterDemo = () => {
  const { t, i18n } = useTranslation();

  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    company_name: "",
    use_case: "",
    language: i18n.language || "en",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const USE_CASES = [
    { value: "clinical", label: t("auth.registerDemo.useCases.clinical") },
    { value: "research", label: t("auth.registerDemo.useCases.research") },
    { value: "education", label: t("auth.registerDemo.useCases.education") },
    { value: "pharma", label: t("auth.registerDemo.useCases.pharma") },
    { value: "biotech", label: t("auth.registerDemo.useCases.biotech") },
    { value: "other", label: t("auth.registerDemo.useCases.other") },
  ];

  // If registration is successful, show success message instead
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>

        <div className="w-full max-w-md">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-md ring-1 ring-gray-100">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl flex items-center justify-center">
                  <img src="/logo.svg" alt="MediLens" className="h-10 w-10" />
                </div>
              </div>
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 bg-clip-text text-transparent tracking-wide font-sans animate-gradient bg-[length:200%_auto] mb-2">
              MediLens
            </h1>
          </div>

          {/* Success Message */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t("auth.registerDemo.successTitle")}
              </h2>

              <p className="text-gray-600 mb-6">
                {t("auth.registerDemo.successMessage")}{" "}
                <span className="font-semibold">{formData.email}</span>
              </p>

              <div className="w-full bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>{t("auth.registerDemo.nextSteps")}</strong>
                  <br />
                  1. {t("auth.registerDemo.step1")}
                  <br />
                  2. {t("auth.registerDemo.step2")}
                  <br />
                  3. {t("auth.registerDemo.step3")}
                  <br />
                  4. {t("auth.registerDemo.step4")}
                </p>
              </div>

              <Link
                to="/login"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("auth.backToLogin")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError("");
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = t("auth.registerDemo.errors.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t("auth.registerDemo.errors.invalidEmail");
    }

    if (!formData.full_name || formData.full_name.trim().length === 0) {
      errors.full_name = t("auth.registerDemo.errors.nameRequired");
    }

    if (!formData.company_name || formData.company_name.trim().length === 0) {
      errors.company_name = t("auth.registerDemo.errors.companyRequired");
    }

    if (!formData.use_case) {
      errors.use_case = t("auth.registerDemo.errors.useCaseRequired");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const apiUrl = getApiUrl();
      await axios.post(
        `${apiUrl}${buildApiPath(API_ENDPOINTS.AUTH.REGISTER_DEMO)}`,
        {
          email: formData.email.toLowerCase().trim(),
          full_name: formData.full_name.trim(),
          company_name: formData.company_name.trim(),
          use_case: formData.use_case,
          language: i18n.language || "en",
        },
      );

      setSuccess(true);
    } catch (err) {
      console.error("Registration error:", err);
      if (err.response?.status === 409) {
        setError(t("auth.registerDemo.errors.emailExists"));
      } else {
        // Handle Pydantic validation errors
        let errorMessage = t("auth.registerDemo.errors.registrationFailed");

        if (err.response?.data?.detail) {
          const detail = err.response.data.detail;

          // If detail is an array of validation errors (Pydantic format)
          if (Array.isArray(detail)) {
            errorMessage = detail.map((e) => e.msg || e.message).join(", ");
          }
          // If detail is a string
          else if (typeof detail === "string") {
            errorMessage = detail;
          }
        }

        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      {/* Language Selector in top-right corner */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>

      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-md ring-1 ring-gray-100">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl flex items-center justify-center">
                <img src="/logo.svg" alt="MediLens" className="h-10 w-10" />
              </div>
            </div>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 bg-clip-text text-transparent tracking-wide font-sans animate-gradient bg-[length:200%_auto]">
            {t("auth.registerDemo.title")}
          </h1>
        </div>

        {/* Registration Form */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t("auth.emailAddress")} *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-3 border ${
                    validationErrors.email
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                  } rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors`}
                  placeholder={t("auth.registerDemo.emailPlaceholder")}
                  disabled={isLoading}
                />
              </div>
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.email}
                </p>
              )}
            </div>

            {/* Full Name Field */}
            <div>
              <label
                htmlFor="full_name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t("auth.registerDemo.fullName")} *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-3 border ${
                    validationErrors.full_name
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                  } rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors`}
                  placeholder={t("auth.registerDemo.fullNamePlaceholder")}
                  disabled={isLoading}
                />
              </div>
              {validationErrors.full_name && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.full_name}
                </p>
              )}
            </div>

            {/* Company Name Field */}
            <div>
              <label
                htmlFor="company_name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t("auth.registerDemo.companyOrganization")} *
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="company_name"
                  name="company_name"
                  type="text"
                  autoComplete="organization"
                  required
                  value={formData.company_name}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-3 border ${
                    validationErrors.company_name
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                  } rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors`}
                  placeholder={t("auth.registerDemo.companyPlaceholder")}
                  disabled={isLoading}
                />
              </div>
              {validationErrors.company_name && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.company_name}
                </p>
              )}
            </div>

            {/* Use Case Dropdown */}
            <div>
              <label
                htmlFor="use_case"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t("auth.registerDemo.useCase")} *
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  id="use_case"
                  name="use_case"
                  required
                  value={formData.use_case}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-3 border ${
                    validationErrors.use_case
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                  } rounded-xl text-gray-900 focus:outline-none focus:ring-2 transition-colors appearance-none`}
                  disabled={isLoading}
                >
                  <option value="">
                    {t("auth.registerDemo.selectUseCase")}
                  </option>
                  {USE_CASES.map((useCase) => (
                    <option key={useCase.value} value={useCase.value}>
                      {useCase.label}
                    </option>
                  ))}
                </select>
              </div>
              {validationErrors.use_case && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.use_case}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                  {t("auth.registerDemo.creatingAccount")}
                </>
              ) : (
                t("auth.registerDemo.getStartedFree")
              )}
            </button>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs text-blue-800 text-center">
                🎉 <strong>{t("auth.registerDemo.noCreditCard")}</strong>{" "}
                {t("auth.registerDemo.freeSearches")}
              </p>
            </div>

            {/* Login Link */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-center text-sm text-gray-600">
                {t("auth.registerDemo.alreadyHaveAccount")}{" "}
                <Link
                  to="/login"
                  className="font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                >
                  {t("auth.signIn")}
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterDemo;
